import { Button, Button as ShadcnButton } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useToast } from '@/components/ui/use-toast'
import { useUserStore } from '@/stores/user'
import {
  Alert,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast as sonnerToast } from 'sonner'
import { IPromotion } from '../../../@types/promotions'
import { Modal } from '../../../components'
import services from '../../../services'
import { ModalUpdateProps } from './@types'
import { useData } from './use-data'

export default function ModalPromo({ open }: ModalUpdateProps) {
  const { clearPromoModal, promoModal: user } = useUserStore()
  const { refetch } = useData()
  const [promotions, setPromotions] = useState<IPromotion[]>([])
  const [selectedPackageId, setSelectedPackageId] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const { toast, dismiss } = useToast()

  const onPromotionChange = (event: SelectChangeEvent) => {
    setSelectedPackageId(event.target.value)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)

      if (selectedPackageId) {
        toast({
          title: 'Confirmar alteração de promoção',
          description:
            'Tem a certeza que pretende alterar a promoção deste utilizador?',
          action: (
            <div className="flex gap-2">
              <ShadcnButton
                onClick={confirmPromotionChange}
                variant="default"
                size="sm"
              >
                Sim
              </ShadcnButton>
              <ShadcnButton
                onClick={() => dismiss()}
                variant="secondary"
                size="sm"
              >
                Não
              </ShadcnButton>
            </div>
          ),
          duration: 999999999,
        })
      }
    } catch (error) {
      setError((error as Error).message)
    }
  }

  const confirmPromotionChange = async () => {
    try {
      setLoading(true)
      dismiss()

      if (!user?.id) throw new Error('ID do utilizador não encontrado')

      await services.promotions.setPromotion({
        userId: user.id,
        packageId: selectedPackageId,
      })

      sonnerToast.success('Promoção atualizada com sucesso.')
      closeModal()
    } catch (error) {
      setError((error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const removePromotion = async () => {
    if (!user?.id) return

    toast({
      title: 'Confirmar remoção de promoção',
      description:
        'Tem a certeza que pretende remover a promoção deste utilizador?',
      action: (
        <div className="flex gap-2">
          <ShadcnButton
            onClick={async () => {
              try {
                setLoading(true)
                dismiss()
                await services.promotions.removePromotion(user.id)
                sonnerToast.success('Promoção removida com sucesso.')
                closeModal()
              } catch (error) {
                setError((error as Error).message)
              } finally {
                setLoading(false)
              }
            }}
            variant="destructive"
            size="sm"
          >
            Sim
          </ShadcnButton>
          <ShadcnButton onClick={() => dismiss()} variant="secondary" size="sm">
            Não
          </ShadcnButton>
        </div>
      ),
      duration: 999999999,
    })
  }

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        const data = await services.promotions.listPromotions()
        if (data) setPromotions(data)
      } catch (error) {
        setError((error as Error).message)
      }
    }

    loadPromotions()
  }, [])

  const closeModal = () => {
    setSelectedPackageId('')
    setError(null)
    refetch()
    clearPromoModal()
  }

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2 className="text-xl font-bold">Gerir Promoção</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-12">
          {loading && <Alert severity="warning">A enviar dados...</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <ScrollArea className="h-[280px] w-full">
              <div className="grid gap-8 my-2">
                <FormControl fullWidth>
                  <InputLabel id="select-promotion" className="!text-white">
                    Promoção
                  </InputLabel>
                  <Select
                    labelId="select-promotion"
                    label="Promoção"
                    value={selectedPackageId}
                    onChange={onPromotionChange}
                    disabled={loading}
                  >
                    {promotions.map((promotion) => (
                      <MenuItem key={promotion.id} value={promotion.id}>
                        {promotion.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </ScrollArea>

            <div className="w-max max-w-full ml-auto mt-2 gap-4 flex">
              <Button
                disabled={loading}
                variant="outline"
                type="reset"
                onClick={closeModal}
              >
                Cancelar
              </Button>

              {user?.signature === 'PROMOTION' && (
                <Button
                  onClick={removePromotion}
                  variant="destructive"
                  type="button"
                  disabled={loading}
                >
                  Remover Promoção
                </Button>
              )}

              <Button disabled={loading} type="submit">
                Guardar
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
